import React, { useState } from "react"
import { SkeletonLines } from "components/new-stuff/ui/Skeleton"
import { dptKRs, IEmployeeKRListing } from "../../interface/department"
import { Dropdown } from "antd"
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline"
import ViewInitiative from "../../individual/ViewInitiative"
import { useGet } from "utils/useFetch"
import { IEmpObjData } from "../../interface/individual"
import { useMutation } from "@tanstack/react-query"
import { newArchAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { queryClient } from "index"
import CreateInitiative from "../../individual/CreateInitiative"
import { KR } from "pages/dashboard/performance/components/objectives/ObjCardComponents"
import { useSearchParams } from "react-router-dom"
import { ViewKR } from "pages/dashboard/performance/components/objectives/ViewKR"
interface props {
  item: IEmpObjData
  pageType: "admin" | "manager" | "employee"
  onDelete: (keyResultId: number) => void
  viewEndpoint: string
  showDropDownActions?: boolean
}

interface ModalState {
  type: "view" | "edit" | "delete" | ""
  data: dptKRs | null
}

const EmployeeKeyResult = ({
  item,
  pageType,
  viewEndpoint,
  showDropDownActions = true,
  onDelete,
}: props) => {
  const [searchParams] = useSearchParams()
  const employeeId = searchParams.get("x")
  const [activeStatusUpdates, setActiveStatusUpdates] = React.useState<
    number | null
  >(null)

  const [selectedKeyResultId, setSelectedKeyResultId] = React.useState<
    number | null
  >(null)
  const [addInitiative, setAddInitiative] = useState<number | null>(null)
  const [modal, setModal] = useState<ModalState>({
    type: "",
    data: null,
  })

  const { isLoading: isLoadingEmpKeyResults, data: EmpkeyResults } =
    useGet<IEmployeeKRListing>({
      url:
        pageType === "admin" || pageType === "manager"
          ? `${viewEndpoint}/${employeeId}/${item?.cycleDurationId}`
          : `${viewEndpoint}/${item?.cycleDurationId}`,
      instanceKey: "newArchAxiosInstance",
      enabled: !!item?.cycleDurationId,
    })

  const {
    isLoading: isSubmitting,
    mutate: submit,
    variables,
  } = useMutation({
    mutationFn: async (body: any) => {
      const endpoint = `/api/v2/employee/okr/keyresultprogress`
      const method = "patch"

      const res = await newArchAxiosInstance.request({
        url: endpoint,
        method,
        data: body,
      })
      return res.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [
          `/api/v2/employee/okr/keyresults/${item?.departmentObjectiveId}/${item?.cycleDurationId}`,
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [
          `/api/v2/admin/okr/employeekeyresults/${item?.departmentObjectiveId}/${employeeId}/${item?.cycleDurationId}`,
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [
          `/api/v2/employee/okr/departmentobjectives/${item.cycleDurationId}`,
        ],
      })
      queryClient.invalidateQueries({
        queryKey: [
          `/api/v2/employee/okr/companyobjectives?cycleDurationId=${item?.cycleDurationId}`,
        ],
      })
      setActiveStatusUpdates(null)
    },
    onError: onQueryError,
  })

  return (
    <div>
      {isLoadingEmpKeyResults ? (
        <div
          className="py-10"
          aria-busy="true"
        >
          <SkeletonLines lines={4} />
        </div>
      ) : (
        <div className=" flex flex-col gap-3">
          {EmpkeyResults?.data?.map((kr, idx) => (
            <div
              className=""
              key={`employee-kr-${kr.employeeKeyResultId}-${idx}`}
            >
              <KR.KRItemWrapper
                loading={activeStatusUpdates == kr?.employeeKeyResultId}
              >
                <KR.KRItemTitle
                  onClick={() =>
                    setSelectedKeyResultId(
                      selectedKeyResultId === kr?.employeeKeyResultId
                        ? null
                        : kr?.employeeKeyResultId
                    )
                  }
                  title={kr?.keyResultTitle || ""}
                />

                <div className="flex items-center gap-2">
                  {kr.metric === 3 ? (
                    <KR.KRSwitch
                      score={kr?.employeeTargetDto[0]?.currentProgress || 0}
                      onChange={(checked) => {
                        setActiveStatusUpdates(kr?.employeeKeyResultId || null)
                        const body = {
                          keyResultId: kr?.employeeKeyResultId,
                          targetId: kr?.employeeTargetDto[0]?.employeeTargetId,
                          currentProgress: checked ? 100 : 0,
                        }
                        submit(body)
                      }}
                      disabled={isSubmitting || pageType === "admin"}
                      loading={
                        variables?.keyResultId === kr?.employeeKeyResultId &&
                        isSubmitting
                      }
                    />
                  ) : (
                    <KR.KRInput
                      viewType="edit"
                      metric={kr?.metric}
                      defaultScore={
                        kr?.employeeTargetDto[0]?.currentProgress || 0
                      }
                      targetValue={kr?.employeeTargetDto[0]?.endValue}
                      onBlur={(value) => {
                        setActiveStatusUpdates(kr?.employeeKeyResultId || null)
                        const body = {
                          keyResultId: kr?.employeeKeyResultId,
                          targetId: kr?.employeeTargetDto[0]?.employeeTargetId,
                          currentProgress: value ? Number(value) : null,
                        }
                        submit(body)
                      }}
                      disabled={isSubmitting || pageType === "admin"}
                    />
                  )}

                  <div className="flex items-center gap-6">
                    <KR.KRDueDate
                      date={kr?.employeeTargetDto[0]?.dueDate || ""}
                    />

                    <KR.KRWeight
                      weight={kr?.weight}
                      isWeightAssigned={kr?.isWeightAssigned}
                    />
                    <KR.KRProgress
                      progress={
                        kr?.employeeTargetDto[0]?.percentageProgress || 0
                      }
                    />

                    <KR.KRActionsBtn
                      onAddInitiative={
                        pageType === "admin" || pageType === "manager"
                          ? undefined
                          : () => {
                              setAddInitiative(kr?.employeeKeyResultId)
                            }
                      }
                      onDelete={
                        pageType === "admin" || pageType === "manager"
                          ? () => {
                              onDelete(kr.employeeKeyResultId)
                            }
                          : undefined
                      }
                      onView={() => {
                        setModal({
                          type: "view",
                          data: kr,
                        })
                      }}
                    />
                  </div>
                </div>
              </KR.KRItemWrapper>

              {selectedKeyResultId === kr?.employeeKeyResultId && (
                <ViewInitiative keyResultId={kr?.employeeKeyResultId} />
              )}
              {addInitiative === kr?.employeeKeyResultId && (
                <CreateInitiative
                  keyResultId={addInitiative}
                  keyResultName={kr?.keyResultTitle || ""}
                  handleClose={() => setAddInitiative(null)}
                  handleSuccess={() => {
                    queryClient.invalidateQueries({
                      queryKey: [
                        `/api/v2/admin/okr/employee/keyresults/initiatives/${kr?.employeeKeyResultId}`,
                      ],
                    })
                    queryClient.invalidateQueries({
                      queryKey: [
                        `/api/v2/employee/okr/initiatives/${kr?.employeeKeyResultId}`,
                      ],
                    })
                  }}
                  pageType={pageType}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {modal.type === "view" && (
        <ViewKR
          onClose={() => {
            setModal({ type: "", data: null })
          }}
          open={true}
          data={modal.data as dptKRs}
          role={pageType}
        />
      )}
    </div>
  )
}

export default EmployeeKeyResult
